import React, { ReactElement } from "react";
import styles from "./BaseCard.module.scss";
import clsx from "clsx";
import { ImageAspectRatio, ImageRatio } from "../ImageRatio/ImageRatio";
import {
  ResponsiveSanityImage,
  ResponsiveSanityImageProps
} from "../ResponsiveSanityImage/ResponsiveSanityImage";
import { isSanityImagePresent } from "../SanityImage";
import { BackgroundColor, classForBackground } from "../Backgrounds/background";

export interface BaseCardProps {
  background?: BackgroundColor;
  className?: string;
  trackingName?: string;
  textAlign?: "left" | "center";
  style?: React.CSSProperties;
}

interface CommonOverrideProps {
  className?: string;
}

export type BaseCardBorderColor = BackgroundColor;
export interface BaseCardImageProps {
  ratio: ImageAspectRatio;
  borderColor?: BaseCardBorderColor;
  extraComponent?: ReactElement;
}

export const BaseCardImage: React.FC<
  React.PropsWithChildren<BaseCardImageProps>
> = ({ children, extraComponent, ratio, borderColor }) => {
  return (
    <div
      className={clsx(styles.image, {
        [styles.imageBorderNeutral05]: borderColor == "neutral05",
        [styles.imageBorderNeutral35]: borderColor == "neutral35"
      })}
    >
      <ImageRatio ratio={ratio}>{children}</ImageRatio>
      {extraComponent}
    </div>
  );
};

export type BaseCardSanityImageProps = Omit<
  ResponsiveSanityImageProps,
  "defaultWidth" | "defaultHeight"
> &
  BaseCardImageProps;

export const BaseCardSanityImage: React.FC<
  React.PropsWithChildren<BaseCardSanityImageProps>
> = ({ ratio, borderColor, children, extraComponent, ...props }) => {
  return (
    <BaseCardImage
      ratio={ratio}
      borderColor={borderColor}
      extraComponent={extraComponent}
    >
      {isSanityImagePresent(props.image) && (
        <ResponsiveSanityImage
          {...props}
          defaultWidth={400}
          defaultHeight={600}
          draggable={false}
          loading="lazy"
        />
      )}
      {children}
    </BaseCardImage>
  );
};

export const BaseCardTitle: React.FC<
  React.PropsWithChildren<CommonOverrideProps>
> = ({ className, children }) => (
  <h3 className={clsx(styles.title, className)}>{children}</h3>
);

export const BaseCardImageFooter: React.FC<
  React.PropsWithChildren<CommonOverrideProps>
> = ({ className, children }) => (
  <div className={clsx(styles.imageFooter, className)}>{children}</div>
);

export const BaseCardBody: React.FC<
  React.PropsWithChildren<CommonOverrideProps>
> = ({ className, children }) => {
  return <div className={clsx(styles.body, className)}>{children}</div>;
};

export const BaseCardFooter: React.FC<
  React.PropsWithChildren<CommonOverrideProps>
> = ({ className, children }) => {
  return <footer className={clsx(styles.footer, className)}>{children}</footer>;
};

export const BaseCard: React.FC<React.PropsWithChildren<BaseCardProps>> = ({
  background = "transparent",
  textAlign = "left",
  className,
  trackingName,
  children,
  style
}) => {
  const bgClass = classForBackground(background);
  return (
    <article
      className={clsx(styles.baseCard, className, bgClass, {
        [styles.textAlignLeft]: textAlign == "left",
        [styles.textAlignCenter]: textAlign == "center"
      })}
      data-content-name={trackingName}
      style={style}
    >
      {children}
    </article>
  );
};
